import {
  ThemeOptions,
  colors,
  createMuiTheme,
  responsiveFontSizes,
} from "@material-ui/core";
import { merge } from "lodash";

import { AA_BRAND_NAME, BSM_BRAND_NAME, THEMES } from "@ds-developer/constants";
import { Settings } from "@ds-developer/models";

import { softShadows } from "./shadows";
import typography from "./typography";

declare module "@material-ui/core/styles/createPalette" {
  interface TypeBackground {
    dark: string;
  }

  interface BrandColors {
    [BSM_BRAND_NAME]: string;
    [AA_BRAND_NAME]: string;
  }

  interface StatusColors {
    none: string;
    red: string;
  }

  interface PaletteOptions {
    brand: BrandColors;
    status: StatusColors;
  }

  interface Palette {
    brand: BrandColors;
    status: StatusColors;
  }
}

declare module "@material-ui/core/styles/createMuiTheme" {
  interface ThemeOptions {
    name: string;
  }
}

const baseConfig = {
  direction: "ltr",
  typography,
  shadows: softShadows,
  palette: {
    brand: {
      [BSM_BRAND_NAME]: "#F9812A",
      [AA_BRAND_NAME]: "#FFCC00",
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 960,
      lg: 1024,
      xl: 1600,
    },
  },
  overrides: {
    MuiChip: {
      root: {
        backgroundColor: "rgba(0,0,0,0.075)",
      },
    },
    MuiDialogTitle: {
      root: {
        padding: "24px",
      },
    },
    MuiBreadcrumbs: {
      separator: {
        color: "#B0BEC5",
        lineHeight: 1,
        fontSize: "22px",
        display: "flex",
        marginLeft: "16px",
        marginRight: "16px",
      },
    },
    MuiCardHeader: {
      root: {
        padding: "16px 16px",
        lineHeight: "24px",
      },
    },
    MuiIconButton: {
      root: {
        color: "#263238",
      },
    },
    MuiAvatar: {
      root: {
        fontSize: "1rem",
        fontWeight: 500,
        width: "40px",
        height: "40px",
      },
    },
    MuiLinearProgress: {
      root: {
        borderRadius: 3,
        overflow: "hidden",
      },
    },
    MuiListItemIcon: {
      root: {
        color: "#263238",
      },
    },
    MuiAlert: {
      filledSuccess: {
        backgroundColor: "#E8F5E9",
      },
      message: {
        color: colors.blueGrey[900],
        fontWeight: 400,
      },
      icon: {
        color: colors.blueGrey[900],
      },
    },
    MuiTypography: {
      overline: {
        lineHeight: "1",
      },
    },
    MuiSwitch: {
      root: {
        "& .Mui-disabled": {
          "&.Mui-checked": {
            color: `#B6D5F4`,

            "&+ .MuiSwitch-track": {
              backgroundColor: "#2A92FA",
            },
          },
        },
      },
    },
    MuiTabs: {
      flexContainer: {
        borderBottom: "1px solid #E0E0E0",
      },
    },
    MuiTableCell: {
      root: {
        height: "56px", // explicit dimension provided by design team
      },
    },
    MuiTab: {
      root: {
        minWidth: "140px", // explicit dimension provided by design team
      },
    },
    MuiTabPanel: {
      root: {
        padding: "24px 0",
      },
    },
  },
};

const themeConfigs: ThemeOptions[] = [
  {
    name: THEMES.LIGHT,
    overrides: {
      MuiInputBase: {
        input: {
          "&::placeholder": {
            opacity: 1,
            color: colors.blueGrey[600],
          },
        },
      },
    },
    palette: {
      type: "light",
      action: {
        active: colors.blueGrey[600],
      },
      background: {
        default: colors.common.white,
        dark: "#f4f6f8",
        paper: colors.common.white,
      },
      primary: {
        main: "#0D73D9",
      },
      secondary: {
        main: "#2A92FA",
      },
      brand: {
        [BSM_BRAND_NAME]: "#F9812A",
        [AA_BRAND_NAME]: "#FFCC00",
      },
      status: {
        none: "rgba(0, 0, 0, 0)",
        red: "rgba(229, 57, 53, 0.5)",
      },
      text: {
        primary: colors.blueGrey[900],
        secondary: colors.blueGrey[600],
      },
    },
    typography: {
      h1: {
        fontSize: "24px",
      },
      h2: {
        fontSize: "20px",
      },
      h3: {
        fontSize: "16px",
      },
      subtitle1: {
        fontSize: "28px",
        fontWeight: 500,
        lineHeight: 1.2,
      },
    },
  },
];

export const createTheme = (settings: Settings) => {
  let themeConfig = themeConfigs.find(theme => theme.name === settings.theme);

  if (!themeConfig) {
    console.warn(new Error(`The theme ${settings.theme} is not valid`));
    [themeConfig] = themeConfigs;
  }

  let theme = createMuiTheme(
    merge({}, baseConfig, themeConfig, {
      direction: settings.direction,
    }),
  );

  if (settings.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
